export const company = {
  page: {
    uploadLogoModalTitle: 'Upload Pharmacy Logo',
    uploadLogoError: 'Could not upload logo.',
    retrieveFileError: 'Could not retrieve file.',
    fetchCompanyError: 'Could not fetch pharmacy details.',
    tabs: {
      setup: 'Setup',
      advanced: 'Advanced',
      featureFlags: 'Feature Flags'
    }
  },
  featureFlagsSection: {
    title: 'Feature Flags',
    description: "Turn this pharmacy's features on or off. Only the flags you change are saved.",
    legend: "Derived manually: flags in this pharmacy's settings that are not in the code's default list (SetupDefaultRelyingPartyValues).",
    columnFlag: 'Flag (AdditionalFeatures)',
    columnValue: 'Value',
    derivedManually: 'Derived manually',
    notSet: 'Not set',
    notToggleable: 'Not an on/off value. Change it in Setup or Advanced.',
    on: 'On',
    off: 'Off',
    save: 'Save',
    successMessage: 'Feature flags saved. Users need to sign in again to see the change.',
    errorMessage: 'Could not save the feature flags',
    loadErrorMessage: 'Could not load the feature flags'
  },
  setupHub: {
    sectionLabels: {
      companyIdentity: 'Pharmacy Information',
      profileContact: 'Profile & contact',
      termsOfAgreement: 'Terms of agreement',
      pioneerrx: 'PioneerRx Integration',
      tenovi: 'Tenovi Device Sync',
      athena: 'Athena Integration',
      clinicalRanges: 'Clinical ranges',
      providers: 'Providers',
      reportingScheduling: 'Reporting & scheduling',
      telehealthAvailability: 'Telehealth availability',
      rpmPrimary: 'RPM Primary',
      generalConfig: 'General Configuration'
    },
    nextUpLabels: {
      companyDetails: 'Pharmacy Details',
      profileContact: 'Profile & Contact',
      termsOfAgreement: 'Terms of Agreement',
      pioneerrx: 'PioneerRx Integration',
      tenovi: 'Tenovi Device Sync',
      athena: 'Athena Integration',
      clinicalRanges: 'Clinical Ranges',
      providers: 'Primary Provider',
      orgHandle: 'Organization Handle',
      telehealthAvailability: 'Telehealth Availability',
      emailReporting: 'Email Reporting Targets',
      rpmPrimary: 'RPM Primary',
      generalConfig: 'General Configuration'
    },
    items: {
      companyDetails: { title: 'Pharmacy Details', description: 'Legal name and timezone used across reports and scheduling.' },
      profileContact: { title: 'Profile & Contact', description: 'Mailing address and the primary contact for this pharmacy.' },
      termsOfAgreement: { title: 'Terms of Agreement', description: 'Signed service agreement for this pharmacy.' },
      pioneerrx: { title: 'PioneerRx Integration', description: 'Pharmacy system connection for prescription data.' },
      tenovi: { title: 'Tenovi Device Sync', description: 'API credentials for remote monitoring devices.' },
      athena: { title: 'Athena Integration', description: 'EHR department mapping for Athena customers.' },
      clinicalRanges: { title: 'Clinical Configuration', description: 'Alert thresholds for blood pressure, heart rate and glucose.' },
      providers: { title: 'Primary Provider', description: 'Assign providers and choose a primary reviewer.' },
      telehealthAvailability: { title: 'Telehealth Availability', description: 'Weekly hours when providers accept telehealth visits.' },
      emailReporting: { title: 'Email Reporting Targets', description: 'Configure daily RPM report email recipients.' },
      rpmPrimary: {
        title: 'RPM Primary',
        description: "Staff member responsible for RPM review; appears on this pharmacy's RPM reports."
      },
      generalConfig: { title: 'General Configuration', description: 'Enable or disable platform features for this pharmacy.' }
    },
    groupTitles: {
      companyInformation: 'Pharmacy Information',
      integrationsAndDevices: 'Integrations & devices',
      providers: 'Providers',
      reportingAndScheduling: 'Reporting & scheduling',
      generalConfiguration: 'General Configuration'
    },
    backToSetup: 'Back to Setup',
    setupBreadcrumb: 'Setup',
    unknownSection: {
      title: "This section doesn't exist",
      description: 'The setup section you followed a link to is no longer available. Go back to Setup to find what you need.'
    }
  },
  setupItem: {
    cta: {
      completed: 'Review',
      inProgress: 'Continue',
      needsAttention: 'Fix now',
      notStarted: 'Start'
    },
    requiredChip: 'REQUIRED',
    view: 'View'
  },
  setupProgressCard: {
    title: 'Setup progress',
    requiredStepsComplete: (complete: number, total: number) => `${complete} of ${total} required steps complete`,
    optionalConfigured: (count: number) => `${count} optional configured`,
    needsAttentionChip: (count: number) => `${count} needs attention`,
    inProgressChip: (count: number) => `${count} in progress`,
    notStartedChip: (count: number) => `${count} not started`,
    nextUp: (label: string) => `Next up: ${label}`
  },
  setupSection: {
    configuredCount: (done: number, total: number) => `${done} of ${total} configured`
  },
  cards: {
    companyDetails: {
      title: 'Pharmacy Details',
      description: 'Legal name and timezone used across reports and scheduling.',
      saveLabel: 'Save details',
      savedMessage: 'Pharmacy details saved.',
      saveErrorMessage: 'Failed to save pharmacy details.',
      idCopiedMessage: 'Pharmacy ID copied.',
      companyNameRequired: 'Pharmacy name is required',
      timezoneRequired: 'Timezone is required',
      companyIdLabel: 'Pharmacy ID',
      companyNameLabel: 'Pharmacy Name',
      timezoneLabel: 'Timezone',
      timezonePlaceholder: 'Select a timezone…',
      goLiveDateLabel: 'Go Live Date'
    },
    address: {
      title: 'Address',
      description: 'Mailing address for this pharmacy.',
      saveLabel: 'Save address',
      savedMessage: 'Address saved.',
      saveErrorMessage: 'Failed to save address.',
      addressLine1Label: 'Address Line 1',
      addressLine2Label: 'Address Line 2',
      cityLabel: 'City',
      stateLabel: 'State',
      zipLabel: 'Zip Code',
      requiredMessage: 'Required',
      invalidZipMessage: 'Enter a 5 digit zip code',
      incompleteMessage: 'Address Line 1, City, State, and a valid Zip Code are required.'
    },
    primaryContact: {
      title: 'Primary Contact',
      description: 'Main contact for notifications about this pharmacy.',
      saveLabel: 'Save contact',
      savedMessage: 'Contact details saved.',
      saveErrorMessage: 'Failed to save contact details.',
      emailInvalid: 'Enter a valid email address',
      emailLabel: 'Email Address',
      emailPlaceholder: 'name@pharmacy.com',
      phoneLabel: 'Phone Number'
    },
    orgHandle: {
      title: 'Organization',
      description: 'Unique handle used in patient-facing links.',
      savedMessage: 'Organization saved.',
      saveErrorMessage: 'Failed to save organization.',
      orgNameLabel: 'Organization Name',
      orgHandleLabel: 'Org Handle Name',
      orgHandlePlaceholder: 'e.g. rpm-pharmacy-1',
      orgHandleHelperText: 'Only lowercase letters, numbers and hyphens. Spaces become hyphens.'
    },
    supportAdmins: {
      title: 'Support Admins',
      description: 'Assign the primary and secondary PSM support admins for this pharmacy.',
      saveLabel: 'Save admins',
      savedMessage: 'Support admins saved.',
      saveErrorMessage: 'Failed to save support admins.',
      primaryAdminLabel: 'Primary Admin',
      secondaryAdminLabel: 'Secondary Admin',
      selectAdminPlaceholder: 'Select an admin…'
    },
    termsOfAgreement: {
      title: 'Terms of Agreement',
      description: 'Signed service agreement for this pharmacy.',
      acceptedOn: (date: string) => `Accepted on ${date}`,
      acceptedBy: (name: string) => `Accepted by ${name}`,
      viewDocument: 'View document',
      rejectedOn: (date: string) => `Rejected on ${date}`,
      rejectedMessage: 'Please contact support to resolve this.',
      notSignedMessage: 'The terms of agreement have not been signed yet. Contact your account manager to proceed.'
    },
    pioneerRx: {
      title: 'PioneerRx Integration',
      description: 'Pharmacy system connection for prescription data.',
      saveLabel: 'Save integration',
      copyUrlFailedMessage: 'Failed to copy URL.',
      updateCredentialsDialogTitle: 'Setup PioneerRx Authentication Credentials',
      usernameRequiredMessage: 'Please enter a username.',
      passwordRequiredMessage: 'Please enter a password.',
      locationNotFoundMessage: 'Client location not found.',
      saveCredentialsErrorMessage: 'Cannot save PioneerRx credentials. Contact support.',
      credentialsSavedMessage: 'PioneerRx credentials saved.',
      integrationConfiguredMessage: 'PioneerRx integration is configured.',
      setCredentialsWarningMessage: 'Set PioneerRx credentials before saving.',
      organizationLabel: 'Organization',
      organizationPlaceholder: 'Select an organisation…',
      clientLocationLabel: 'Client Location',
      clientLocationPlaceholder: 'Select a location…',
      noLocationsHelperText: 'No locations for this organisation',
      webhookUrlLabel: 'PioneerRx Webhook URL',
      copiedTooltip: 'Copied!',
      copyUrlTooltip: 'Copy URL',
      apiCredentialsTitle: 'API Credentials',
      apiCredentialsDescription: 'Username and password PioneerRx uses to push data to this pharmacy.',
      credentialsStoredMessage: 'Credentials stored',
      updateCredentialsButton: 'Update credentials',
      setCredentialsButton: 'Set credentials'
    },
    tenovi: {
      title: 'Tenovi Device Sync',
      description: 'API credentials for remote monitoring devices.',
      saveLabel: 'Save configuration',
      savedMessage: 'Tenovi configuration saved.',
      saveErrorMessage: 'Failed to save Tenovi configuration.',
      clientNameRequired: 'Client name is required',
      apiKeyRequired: 'API key is required',
      clientNameLabel: 'Tenovi Client Name',
      apiKeyLabel: 'Tenovi API Key',
      apiKeyPlaceholder: 'Paste API key',
      secondaryApiKeyLabel: 'Tenovi Secondary API Key',
      secondaryApiKeyPlaceholder: 'Optional'
    },
    athena: {
      title: 'Athena Integration',
      description: 'EHR department mapping for pharmacies on Athena.',
      savedMessage: 'Athena configuration saved.',
      saveErrorMessage: 'Failed to save Athena configuration.',
      departmentIdLabel: 'Athena Department Id',
      departmentIdPlaceholder: 'e.g. 118'
    },
    athenaSupervisingProviders: {
      title: 'Provider Supervising IDs',
      description: 'Optional Athena supervising provider id for each provider admin in this pharmacy.',
      savedMessage: 'Supervising provider id saved.',
      saveErrorMessage: 'Failed to save supervising provider id.',
      saveTooltip: 'Save supervising provider id',
      idPlaceholder: 'e.g. 12345',
      nameColumn: 'Name',
      emailColumn: 'Email',
      supervisingIdColumn: 'Supervising Provider Id'
    },
    clinicalRanges: {
      title: 'Clinical Ranges',
      description: 'Alert thresholds for blood pressure, heart rate and glucose.',
      updateErrorMessage: 'Could not update clinical ranges.',
      bloodPressureHeading: 'Blood Pressure',
      systolicLabel: 'Systolic',
      diastolicLabel: 'Diastolic',
      heartRateLabel: 'Heart Rate',
      bloodGlucoseLabel: 'Blood Glucose',
      units: { mmHg: 'mmHg', bpm: 'bpm', mgDl: 'mg/dl' },
      criticallyLowLabel: 'Critically Low',
      lowLabel: 'Low',
      highLabel: 'High',
      criticallyHighLabel: 'Critically High',
      valuesIncreaseHint: 'Values must increase left to right',
      allowedRange: (min: number, max: number) => `Allowed ${min}–${max}`,
      requiredError: 'Required',
      rangeError: (min: number, max: number) => `Must be ${min}–${max}`,
      orderError: 'Out of order',
      resetDialogTitle: 'Reset Alert Limits',
      resetDialogMessage: 'Please select how you would like to reset the alert limits',
      resetCompanyAction: 'Reset Pharmacy',
      resetCompanyAndPatientsAction: 'Reset Pharmacy and Patients',
      cancelAction: 'Cancel',
      resetMenuLabel: 'Reset',
      resetChangesOption: 'Reset changes',
      resetToDefaultOption: 'Reset to default',
      saveMenuLabel: 'Save',
      saveOption: 'Save',
      saveAndApplyToAllOption: 'Save & apply to all'
    },
    telehealthAvailability: {
      title: 'Telehealth Availability',
      description: 'Weekly hours when a provider is available for telehealth visits. All times are Eastern Time (ET).',
      saveLabel: 'Save availability',
      savedMessage: 'Availability saved.'
    },
    coverLetter: {
      title: 'Report Cover Letter',
      description: 'Cover letter included at the start of RPM reports for this pharmacy.',
      saveLabel: 'Save details',
      resetLabel: 'Reset form',
      savedMessage: 'Pharmacy RPM report cover letter updated successfully.',
      saveErrorMessage: 'Could not update pharmacy RPM report cover letter.'
    }
  },
  generalConfig: {
    title: 'General Configuration',
    description: 'Enable or disable platform features for this pharmacy.',
    coreFeaturesHeading: 'Core Features',
    rpmKpiLabel: 'RPM KPI',
    aiCoPilotLabel: 'AI CoPilot',
    ccmBillingLabel: 'CCM Billing',
    ccmOnlyEnrollmentLabel: 'CCM-Only Enrollment',
    ccmOnlyEnrollmentSuperAdminOnly: 'CCM-Only Enrollment is turned on one pharmacy at a time by a Truentity super admin.',
    noReadingsTextRemindersLabel: 'No Readings Text Reminders',
    defaultAiSummaryTypeLabel: 'Default AI Summary Type',
    deviceAiSummaryOption: 'Device AI Summary',
    allDevicesAiSummaryOption: 'All Devices AI Summary',
    noAiSummaryOption: 'No AI Summary',
    patientPrioritizationLabel: 'Patient Prioritization',
    patientPrioritizationInsuranceLabel: 'Patient Prioritization Insurance',
    pioneerRxLabel: 'PioneerRx',
    patientOnboardingLabel: 'Patient Onboarding',
    providerOnboardingLabel: 'Provider Onboarding',
    rolloutGroupHeading: 'Rollout Group',
    canaryRolloutLabel: 'Canary rollout',
    canaryRolloutDescription: 'Put this pharmacy on the canary build. Reversible at any time -- turning it off moves them back to stable.',
    engagementTimeHeading: 'Engagement Time',
    engagementTimeLabel: 'Live page and care time tracking',
    engagementTimeDescription:
      "Records how long this pharmacy's staff are active on the portal and on each patient, and shows it on the patient page and the admin dashboard. Turning it off stops recording and hides it; existing records are kept.",
    savedMessage: 'General configuration saved successfully',
    saveErrorMessage: 'Could not save general configuration'
  },
  providersSection: {
    assignProviders: 'ASSIGN PROVIDERS',
    unassignConfirmTitle: 'Unassign Provider',
    unassignConfirmMessage: 'Are you sure you want to unassign this provider?',
    updateProviderTitle: 'Update Provider',
    unassignErrorMessage: 'Error while unassign provider',
    noProvidersMessage: 'No providers assigned yet.',
    tableViewLabel: 'Table view',
    listViewLabel: 'List view',
    columns: {
      firstName: 'First Name',
      lastName: 'Last Name',
      npiNumber: 'NPI Number',
      email: 'Email',
      rpmReviewer: 'RPM Reviewer',
      primary: 'Primary',
      phone: 'Phone',
      fax: 'Fax',
      action: 'Action'
    }
  },
  manageProvidersDialog: {
    title: (fullName: string) => `Providers Assignments for ${fullName}`,
    assignProviders: 'Assign Provider(s)',
    searchPlaceholder: 'Search Provider by First Name, Last Name, NPI',
    noProviderFoundPrefix: "Provider you're looking for is not available here, please ",
    addNewProviderLinkLabel: 'Add a New Provider',
    noProviderFoundSuffix: ' first.',
    requiredFieldsMessage: 'Please fill out all required fields',
    updateSuccessMessage: 'Successfully updated provider',
    updateErrorMessage: 'Could not update provider details',
    noRecordsFoundMessage: 'No Records Found',
    fetchProvidersErrorMessage: 'Unable to retrieve providers',
    lookupRequiredMessage: 'First Name, Last Name, NPI number is required',
    assignSuccessMessage: 'Successfully assigned providers',
    assignErrorMessage: 'Request failed',
    editActionLabel: 'Edit',
    columns: {
      firstName: 'First Name',
      lastName: 'Last Name',
      npiNumber: 'NPI Number',
      email: 'Email',
      phone: 'Phone',
      fax: 'Fax',
      actions: 'Actions'
    }
  },
  settingsSection: {
    systemConfigTitle: 'System Configurations',
    systemConfigDescription: 'Advanced configuration for this pharmacy. Edit as a form or as raw JSON.',
    textTab: 'Text',
    jsonTab: 'JSON',
    saveConfigurationLabel: 'Save configuration',
    mockCompanyTitle: 'Mock Pharmacy',
    mockCompanyDescription:
      'Marks this as a test/mock pharmacy. Mock pharmacies are excluded from reporting, analytics, claims, and notifications.',
    isMockLabel: 'Is Mock',
    dangerZoneTitle: 'Danger Zone',
    activeChip: 'Active',
    inactiveChip: 'Inactive',
    deactivateThisCompanyHeading: 'Deactivate this pharmacy',
    activateThisCompanyHeading: 'Activate this pharmacy',
    deactivateDescription: 'Stops device syncing and removes portal access for providers and staff. Patient records are retained.',
    deactivateLabel: 'Deactivate',
    activateLabel: 'Activate',
    deactivateDialogTitle: 'Deactivate Pharmacy',
    deactivateDialogMessage: (companyName: string) =>
      `<b>Are you sure you want to deactivate the pharmacy: ${companyName}?</b><br /> Deactivating this pharmacy will disable its access and associated functionalities. This action can be reversed by reactivating the pharmacy later.`,
    activateDialogTitle: 'Activate Pharmacy',
    activateDialogMessage: (companyName: string) =>
      `<b>Are you sure you want to activate the pharmacy: ${companyName}?</b><br /> Activating this pharmacy will enable its access and associated functionalities.`,
    deactivatedSuccessMessage: 'Pharmacy deactivated successfully',
    deactivateErrorMessage: 'Could not deactivate pharmacy',
    activatedSuccessMessage: 'Pharmacy activated successfully',
    activateErrorMessage: 'Could not activate pharmacy',
    markedMockMessage: 'Pharmacy marked as mock',
    unmarkedMockMessage: 'Pharmacy unmarked as mock',
    mockStatusErrorMessage: 'Could not update mock status',
    settingsUpdatedMessage: 'Pharmacy settings are updated successfully',
    settingsUpdateErrorMessage: 'Could not update pharmacy settings'
  }
} as const;
